(() => {
  'use strict';
  if (window.SomnilaSn83Catalogue) return;
  const instances = new Map();

  class CatalogueCarousel {
    constructor(section) {
      this.section = section;
      this.grid = section.querySelector('[data-sn83-catalogue-grid]');
      this.controls = section.querySelector('[data-sn83-catalogue-controls]');
      this.previous = section.querySelector('[data-sn83-scroll="-1"]');
      this.next = section.querySelector('[data-sn83-scroll="1"]');
      this.items = [...this.grid.querySelectorAll('[data-sn33-item]')];
      this.abort = new AbortController();
      this.mobile = window.matchMedia('(max-width: 599px)');
      const signal = this.abort.signal;
      this.grid.addEventListener('scroll', () => this.schedule(), { passive: true, signal });
      this.grid.addEventListener('focusin', event => this.alignFocusedItem(event.target), { signal });
      this.grid.addEventListener('keydown', event => {
        if (event.target !== this.grid || event.defaultPrevented || !this.active || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault();
        this.step(event.key === 'ArrowLeft' ? -1 : 1);
      }, { signal });
      this.previous.addEventListener('click', () => this.step(-1), { signal });
      this.next.addEventListener('click', () => this.step(1), { signal });
      window.addEventListener('resize', () => this.schedule(), { passive: true, signal });
      this.filterObserver = new MutationObserver(() => this.schedule(true));
      this.items.forEach(item => this.filterObserver.observe(item, { attributes: true, attributeFilter: ['hidden'] }));
      if (typeof ResizeObserver === 'function') {
        this.resizeObserver = new ResizeObserver(() => this.schedule());
        this.resizeObserver.observe(this.grid);
      }
      this.update();
    }

    visibleItems() { return this.items.filter(item => !item.hidden); }

    update() {
      const maximum = Math.max(0, this.grid.scrollWidth - this.grid.clientWidth);
      const left = Math.max(0, Math.min(maximum, this.grid.scrollLeft));
      this.active = this.mobile.matches && this.visibleItems().length > 1 && maximum > 2;
      this.controls.hidden = !this.active;
      this.grid.tabIndex = this.active ? 0 : -1;
      this.previous.disabled = !this.active || left <= 2;
      this.next.disabled = !this.active || left >= maximum - 2;
    }

    schedule(reset = false) {
      this.resetPending = this.resetPending || reset;
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        if (this.resetPending) {
          this.grid.scrollTo({ left: 0, behavior: 'instant' });
          this.resetPending = false;
        }
        this.update();
      });
    }

    alignFocusedItem(target) {
      if (!this.mobile.matches) return;
      const item = target.closest?.('[data-sn33-item]');
      if (!item || !this.grid.contains(item) || item.hidden) return;
      if (this.focusFrame) cancelAnimationFrame(this.focusFrame);
      this.focusFrame = requestAnimationFrame(() => {
        this.focusFrame = null;
        if (this.abort.signal.aborted || !item.contains(document.activeElement)) return;
        this.update();
        const items = this.visibleItems();
        if (!this.active || !items.includes(item)) return;
        const maximum = Math.max(0, this.grid.scrollWidth - this.grid.clientWidth);
        const left = Math.max(0, Math.min(maximum, item.getBoundingClientRect().left - items[0].getBoundingClientRect().left));
        if (Math.abs(this.grid.scrollLeft - left) > 1) {
          this.grid.scrollTo({ left, behavior: 'instant' });
        }
      });
    }

    step(direction) {
      this.update();
      if (!this.active) return;
      const items = this.visibleItems();
      const maximum = Math.max(0, this.grid.scrollWidth - this.grid.clientWidth);
      const left = Math.max(0, Math.min(maximum, this.grid.scrollLeft));
      const first = items[0].getBoundingClientRect().left;
      const positions = [...new Set([0, ...items.map(item => Math.max(0, Math.min(maximum, item.getBoundingClientRect().left - first))), maximum])].sort((a, b) => a - b);
      const target = direction > 0
        ? positions.find(position => position > left + 2) ?? maximum
        : [...positions].reverse().find(position => position < left - 2) ?? 0;
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.grid.scrollTo({ left: target, behavior: reducedMotion ? 'instant' : 'smooth' });
    }

    destroy() {
      this.abort.abort();
      this.filterObserver.disconnect();
      this.resizeObserver?.disconnect();
      if (this.frame) cancelAnimationFrame(this.frame);
      if (this.focusFrame) cancelAnimationFrame(this.focusFrame);
      this.controls.hidden = true;
      this.grid.tabIndex = -1;
    }
  }

  const initialise = scope => {
    const sections = [...scope.querySelectorAll('[data-sn33-catalogue]')];
    if (scope.matches?.('[data-sn33-catalogue]')) sections.unshift(scope);
    sections.forEach(section => {
      if (instances.has(section) || !section.querySelector('[data-sn83-catalogue-grid]')) return;
      instances.set(section, new CatalogueCarousel(section));
    });
  };
  const destroy = scope => {
    instances.forEach((instance, section) => {
      if (section === scope || scope.contains(section)) {
        instance.destroy();
        instances.delete(section);
      }
    });
  };
  window.SomnilaSn83Catalogue = { initialise, destroy };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialise(document), { once: true });
  else initialise(document);
  document.addEventListener('shopify:section:load', event => initialise(event.target));
  document.addEventListener('shopify:section:unload', event => destroy(event.target));
})();
